import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
// ⚠️ Обычный clone() на SkinnedMesh НЕ работает: копия остаётся привязанной к
// скелету ОРИГИНАЛА и рисуется там же, где он, — в кадре персонаж просто
// «не появляется». Клонировать скиннинговое только этим.
import { clone as skinClone } from 'three/examples/jsm/utils/SkeletonUtils.js';

// ============================================================
//  Человеческая модель вместо коробок R15.
//
//  ГЛАВНОЕ РЕШЕНИЕ, которое объясняет весь файл: **второй анимации здесь нет**.
//  Ходьбу, присед, ползание, наклон и хват по-прежнему считает `remote.js` на
//  своём риге из шарниров — модель лишь ПОВТОРЯЕТ его позу костями. Иначе у
//  нас появились бы две анимации (коробочная и скелетная), они разъехались бы
//  при первой же правке, а заметить это можно было бы только с двух
//  компьютеров сразу — ровно та ловушка, от которой уходили в `body.js`.
//
//  Отсюда устройство: рядом с невидимым ригом-шарнирами живёт скелет модели,
//  и каждый кадр `syncSkin` кладёт на кости ПОВОРОТ соответствующего шарнира
//  рига — целиком, а не только направление сегмента. Разницу систем («у нас
//  сегмент висит вдоль −Y, у кости ребёнок сидит вдоль +Y, и поворот покоя
//  у каждой кости свой») снимает константа `skin.fix`, посчитанная один раз
//  по позе привязки.
//
//  ⚠️ Наводить кость ОДНИМ НАПРАВЛЕНИЕМ (как было до 2026-07-29) нельзя:
//  направление не задаёт поворот вокруг самой кости, и тот доставался
//  «минимальным поворотом» — а он у вертикальной ноги вырожден и вдобавок
//  зависит от курса игрока. Ступни от этого разворачивало вдвое сильнее тела.
//  Разбор — баг-урок в md/models.md 2.3.10.
//
//  ⚠️ Модель смотрит в +Z, а наше тело — в −Z (нос-указатель, `placePart`,
//  `prone_clear` на сервере). Поэтому корень скелета развёрнут на π вокруг Y.
//  Побочно это меняет местами лево и право, из-за чего кости `_l` ложатся на
//  нашу левую руку (−X) сами собой — совпадение приятное, но не случайное.
//
//  Откат к коробкам — `?body=boxes` в адресе (см. `useSkin` в remote.js).
// ============================================================

const MODEL = '/models/character.glb';

// Локальные копии чисел из remote.js/state.js. ⚠️ Импортировать их оттуда
// нельзя: `remote.js` импортирует ЭТОТ файл, и обратный импорт замкнул бы цикл
// модулей — тот самый класс TDZ-ошибок, из-за которого однажды не загрузился
// весь клиент (баг-урок в md/models.md 2.3.4). Разъедутся — наклон и кивок
// у модели перестанут совпадать с коробочным телом; ловится только глазами.
const LEAN_OFFSET_LOCAL = 0.5;      // ↔ LEAN_OFFSET в state.js/movement.rs
const SPINE_ROLL = 0.22;            // ↔ LEAN_ROLL в remote.js
// Сумма кивка шеи и головы = 0.35 — ровно та доля, что у коробочной головы
// (HEAD_PITCH_SHARE): она зеркало хитбокса, а хитбокс на питч не реагирует.
//
// ⚠️ РАЗДЕЛЕНИЕ внутри этой суммы — не вкус, а ЦЕНА В САНТИМЕТРАХ. Кость
// головы крутит череп вокруг точки почти в его центре (плечо ≈0.10 м), шея —
// вокруг точки на 0.20 м ниже. То есть один и тот же видимый кивок шеей
// уносит голову из коробки хедшота ВДВОЕ дальше, чем головой. Замер `#check`
// (доля 0.22/0.13 → 0.28/0.07): голова вне коробок 0.168 → 0.128 м на предельном
// питче при том же суммарном кивке. Крутить только с прогоном.
const HEAD_PITCH_SHARE_LOCAL = 0.28;
const NECK_PITCH_SHARE = 0.07;

/** Размеры рига, снятые с модели (`tools/character-pack/rig.mjs`).
 *
 *  ⚠️ Это НЕ хитбоксы и не баланс: серверные коробки считаются от высоты глаз
 *  и не меняются ни на байт. Здесь только то, где у КОНКРЕТНОЙ модели плечо,
 *  бедро и какой длины сегменты, — чтобы ИК ставила кисть на цевьё, а ступню
 *  на пол. Возьмёшь чужую модель — прогони `rig.mjs` и подставь её числа.
 *
 *  Чем отличается от коробочного рига (`BOX_RIG` в remote.js) и почему это
 *  правильно: у модели бедро выше (0.971 против 0.90), плечо НИЖЕ (1.455
 *  против 1.54) и рука короче (0.495 против 0.54). Наши числа выводились из
 *  границ хитбоксов, а не из анатомии; у модели они анатомические. */
export const SKIN_RIG = {
  hipDrop: 0.729,          // на сколько бедро ниже глаз (1.700 − 0.971)
  shoulderDrop: 0.245,     // на сколько плечо ниже глаз (1.700 − 1.455)
  armX: 0.212,             // полуразмах плеч
  legSpan: 0.114,          // полуразнос бёдер
  legSegs: [0.429, 0.459, 0.086],   // бедро / голень / ступня
  armSegs: [0.251, 0.244, 0.100],   // плечо / предплечье / кисть до хвата
  ankleY: 0.086,
  armFull: 0.251 + 0.244 + 0.100,
  // Оружие опускаем вслед за плечом (1.540 − 1.455) и придвигаем к телу
  // на отношение длин рук (0.495/0.540): иначе точка хвата вне досягаемости
  // и руки вытягиваются в струну — см. комментарий в remote.js::applyHands.
  handDrop: 0.085,
  handReach: 0.90,
};


// Какая кость повторяет какой шарнир рига. Порядок важен: родитель раньше
// ребёнка, иначе поворот родителя перетрёт уже поставленного ребёнка.
const CHAINS = [
  ['legL', ['thigh_l', 'calf_l', 'foot_l']],
  ['legR', ['thigh_r', 'calf_r', 'foot_r']],
  ['armL', ['upperarm_l', 'lowerarm_l', 'hand_l']],
  ['armR', ['upperarm_r', 'lowerarm_r', 'hand_r']],
];

// ---- Загрузка шаблона (один раз на всех) ----
let tpl = null;
const pending = [];

/** Грузит модель один раз. Пока не готова — тело остаётся коробочным и
 *  подменяется на месте, как только шаблон приехал (тот же приём, что у
 *  мировых моделей оружия в `remote.js::worldPending`). */
export function loadCharacter() {
  if (tpl !== null) return;
  tpl = false;                                  // «уже грузим»
  new GLTFLoader().load(MODEL, (gltf) => {
    tpl = gltf.scene;
    tpl.updateMatrixWorld(true);
    markParts(tpl);
    for (const p of pending.splice(0)) attach(p);
  }, undefined, (e) => {
    console.error('[модель персонажа] не загрузилась, остаёмся на коробках', e);
    tpl = false;
  });
}

// ============================================================
//  Разметка вершин по частям тела (атрибут `aPart`)
//
//  Зачем: у модели ОДИН скиннинговый меш на всё тело, и двум задачам нужно
//  различать его куски прямо в шейдере:
//   • своё тело от первого лица показывает ТОЛЬКО ноги (`body.js`);
//   • голова обязана быть светлее тела — зона хедшота читается глазом
//     (у коробок так и было, см. `KIT_PLAYER`).
//  Материалом ни то, ни другое не сделать: материал у меша один на всё тело.
//
//  ⚠️ Разметка по КОСТИ, а не по высоте вершины. Высота в позе привязки
//  выглядит проще, но не работает: у модели руки висят вдоль тела, и кисть
//  оказывается на уровне бедра (плечо 1.455 − длина руки 0.595 ≈ 0.86 при
//  тазе 0.971). Любая горизонтальная отсечка «оставить ноги» либо срезала бы
//  бёдра, либо оставляла в кадре висящие в воздухе кисти. Кость же говорит,
//  ЧТО это за место, а не где оно случайно оказалось.
//
//  Считается ОДИН раз на шаблоне: геометрия у клонов общая (`SkeletonUtils`
//  копирует скелет, но не меши геометрии), поэтому атрибут достаётся всем.
// ============================================================
// ⚠️ Признаков ДВА и каждый 0/1 (`aPart.x` — нога, `aPart.y` — голова), а не
// один номер части. Это не стиль, а БАГ-УРОК: varying интерполируется по
// треугольнику, и на шве «таз→бедро» номер честно проезжает через значение
// «нога» — то есть кусок шва остаётся видимым. Стоя внутри собственной головы
// (камера от первого лица именно там) этот поясок ближе всего к камере и
// растягивается ДУГОЙ НА ПОЛЭКРАНА. С признаками 0/1 порог ставится на
// 0.999: любая примесь соседней части даёт меньше единицы, и шов честно
// уходит целиком. Ошибаемся в безопасную сторону — режем чуть больше,
// подсвечиваем чуть меньше.
//
// ⚠️ Шея НЕ голова: зона хедшота на сервере — это 0.22 м вокруг глаз
// (`hit.rs`), а кость шеи держит горло заметно ниже. Подсветив шею, мы
// нарисовали бы зону больше настоящей — то есть соврали бы стреляющему.
const RE_HEAD = /^head/i;
const RE_LEG = /^(thigh|calf|foot|ball)/i;

const PART_KEYS = ['getX', 'getY', 'getZ', 'getW'];

function markParts(root) {
  root.traverse((o) => {
    if (!o.isSkinnedMesh || o.geometry.getAttribute('aPart')) return;
    const g = o.geometry;
    const si = g.attributes.skinIndex, sw = g.attributes.skinWeight;
    if (!si || !sw) return;
    const leg = o.skeleton.bones.map((b) => RE_LEG.test(b.name));
    const head = o.skeleton.bones.map((b) => RE_HEAD.test(b.name));
    const n = g.attributes.position.count;
    const out = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      // Вершину относим к кости с НАИБОЛЬШИМ весом: на швах (бедро/таз)
      // вес делится, и «главная» кость — единственный однозначный ответ.
      let best = -1, bone = 0;
      for (const c of PART_KEYS) {
        const w = sw[c](i);
        if (w > best) { best = w; bone = si[c](i); }
      }
      out[i * 2] = leg[bone] ? 1 : 0;
      out[i * 2 + 1] = head[bone] ? 1 : 0;
    }
    g.setAttribute('aPart', new THREE.BufferAttribute(out, 2));
  });
}

// ---- Патч шейдера по этой разметке ----
//
// ⚠️ Патчим ТОЛЬКО основной материал. Тень three рисует своим материалом
// глубины, и он остаётся нетронутым — то есть тело от первого лица режется
// для глаза, а тень отбрасывает ЦЕЛУЮ. Это ровно то свойство, ради которого
// в `body.js` заведён `GHOST`, только теперь оно достаётся даром.
//
// ⚠️ `customProgramCacheKey` обязателен: без него three считает два материала
// с одинаковыми настройками взаимозаменяемыми и подсунет одну программу на
// оба — патч молча достанется не тому.
const CUT_LEGS = 'if (vPart.x < 0.999) discard;';
const TINT_HEAD = 'if (vPart.y > 0.999) diffuseColor.rgb = uHead;';

/** Навесить разметку частей на материал.
 *
 *  `head` — цвет головы (живой `THREE.Color` из набора, а не копия: один
 *  источник правды с коробочным телом). `legsOnly` — оставить только ноги.
 *
 *  Материал без атрибута `aPart` (коробки R15, они же откат `?body=boxes`)
 *  читает 0 = «корпус» и не меняется вовсе — patch безопасен на общем наборе. */
export function patchSkin(mat, { head = null, legsOnly = false } = {}) {
  mat.onBeforeCompile = (shader) => {
    if (head) shader.uniforms.uHead = { value: head };
    shader.vertexShader = 'attribute vec2 aPart;\nvarying vec2 vPart;\n'
      + shader.vertexShader.replace('#include <begin_vertex>',
        'vPart = aPart;\n#include <begin_vertex>');
    shader.fragmentShader = `${head ? 'uniform vec3 uHead;\n' : ''}varying vec2 vPart;\n`
      + shader.fragmentShader
        .replace('#include <clipping_planes_fragment>',
          `#include <clipping_planes_fragment>\n${legsOnly ? CUT_LEGS : ''}`)
        .replace('#include <color_fragment>',
          `#include <color_fragment>\n${head ? TINT_HEAD : ''}`);
  };
  mat.customProgramCacheKey = () => `skin${head ? 'H' : ''}${legsOnly ? 'L' : ''}`;
  mat.needsUpdate = true;
  return mat;
}

/** Набор материалов для тела ОТ ПЕРВОГО ЛИЦА: всё тело одним обрезанным
 *  материалом. Один на все меши модели (кожа, глаза, брови) намеренно —
 *  видны должны быть только ноги, а всё остальное режется одинаково.
 *
 *  ⚠️ `DoubleSide` обязателен, и это не украшательство. Отсечка вскрывает
 *  меш: бёдра остаются открытыми трубами, а посмотреть себе под ноги игрок
 *  может в любой момент — при отбраковке задних граней он видел бы В СЕБЯ
 *  чёрную дыру. Изнанка закрывает срез телом (three сам переворачивает
 *  нормаль у задней грани, поэтому она ещё и освещена правильно). Цена —
 *  вдвое больше граней у ОДНОЙ модели на экране, своей. */
export function firstPersonKit(kit) {
  const cut = patchSkin(kit.body.clone(), { legsOnly: true });
  cut.side = THREE.DoubleSide;
  return { head: cut, body: cut, limb: cut };
}

/** Чем красить какой меш модели. Имена — из GLB (узлы `Eyebrows`/`Eyes`/тело).
 *
 *  Смысл цвета прежний и он игровой, а не декоративный: красный силуэт —
 *  живой противник, синий — бот. Поэтому кожа модели красится набором
 *  (`KIT_PLAYER`/`KIT_NPC`), а не своей текстурой; текстуры из пака в игру
 *  и не поехали (см. `tools/character-pack/to-glb.mjs`). */
const MAT = {};
function paintKey(skin) {
  MAT.Eyes = skin.kit.head;          // светлее тела — заодно читается лицо
  MAT.Eyebrows = skin.kit.limb;      // темнее тела
}

/** Ставит скелет модели в уже собранное тело. */
function attach(skin) {
  if (!tpl || skin.dead) return;
  paintKey(skin);
  const model = skinClone(tpl);
  // ⚠️ Разворот на π: модель смотрит в +Z, наше тело — в −Z (см. шапку).
  model.rotation.y = Math.PI;

  const bones = {};
  model.traverse((o) => {
    if (o.isBone) bones[o.name] = o;
    if (!o.isMesh && !o.isSkinnedMesh) return;
    // ⚠️ frustumCulled = false обязателен: three считает габариты
    // скиннингового меша по позе ПРИВЯЗКИ, а мы гнём его костями далеко за
    // её пределы (лёжа тело уходит на 1.7 м назад) — иначе персонаж пропадает
    // на краю экрана в самый неподходящий момент.
    o.frustumCulled = false;
    o.castShadow = true;
    o.material = MAT[o.name] || skin.kit.body;
    // ⚠️ Модель из рейкаста ИСКЛЮЧЕНА. Луч выстрела на клиенте (`bodiesAlong`)
    // рисует декаль, брызги и конец трассера — он обязан идти по той же
    // геометрии, по которой сервер считает урон, то есть по коробкам. Кожа
    // модели уже коробок и вылезает плечами наружу: попади луч в плечо, клиент
    // нарисовал бы попадание там, где сервер урона не засчитает. Плюс цена —
    // 14 тыс. треугольников со скиннингом на каждый выстрел вместо 15 коробок.
    o.raycast = () => {};
  });

  skin.model = model;
  skin.bones = bones;
  // ⚠️ Константа перехода «шарнир рига → кость», по одной на кость. Без неё
  // кость приходилось наводить ОДНИМ направлением, а направление не задаёт
  // поворот вокруг самой кости — и он получался каким угодно (баг-урок
  // «ступни развёрнуты и зависят от курса» в md/models.md 2.3.10).
  //
  // Считается в позе ПРИВЯЗКИ и один раз: берём ориентацию кости, доворачиваем
  // её ось строго вниз (у рига сегмент висит вдоль −Y, у модели рука в T-позе
  // лежит вдоль X) и запоминаем результат. Это и есть «как выглядит кость,
  // когда шарнир рига в нуле»: дальше остаётся домножить на ориентацию шарнира.
  //
  // ⚠️ Меряем ДО `group.add(model)` намеренно: модель ещё сама себе корень,
  // поэтому её мировая ориентация — это система ТЕЛА (разворот корня на π уже
  // учтён, поворот группы по курсу — ещё нет). Ровно та система, в которой
  // потом стоит риг.
  model.updateMatrixWorld(true);
  skin.fix = {};
  for (const [, names] of CHAINS) {
    for (const n of names) {
      if (!bones[n]) continue;
      const q = bones[n].getWorldQuaternion(new THREE.Quaternion());
      const axis = new THREE.Vector3(0, 1, 0).applyQuaternion(q);
      skin.fix[n] = new THREE.Quaternion().setFromUnitVectors(axis, SEG_DIR).multiply(q);
    }
  }
  // ⚠️ Позы ПОКОЯ позвоночника, шеи и головы сохраняем отдельно. У костей
  // поворот покоя не нулевой (у spine_03, например, −14°), и запись
  // `bone.rotation.x = угол` его стирает — тело слегка складывается само собой,
  // а найти это потом невозможно: выглядит как «модель кривая». Поэтому
  // добавка всегда КОМПОНУЕТСЯ с покоем, а не подменяет его.
  skin.rest = {};
  for (const n of ['spine_01', 'spine_02', 'spine_03', 'neck_01', 'Head',
    'foot_l', 'foot_r', 'hand_l', 'hand_r']) {
    if (bones[n]) skin.rest[n] = bones[n].quaternion.clone();
  }
  skin.chains = CHAINS.map(([limb, names]) => [limb, names.map((n) => bones[n])]);
  skin.root = model;
  skin.ready = CHAINS.every(([, names]) => names.every((n) => bones[n]));
  if (!skin.ready) {
    console.error('[модель персонажа] в скелете нет нужных костей — откат на коробки');
    return;
  }
  skin.group.add(model);
  for (const m of skin.hide) m.visible = false;         // коробки прочь
}

/** Заводит скиннинговое тело внутри уже собранной группы R15.
 *
 *  `hide` — коробочные меши, которые надо погасить, когда модель приедет.
 *  Гасим ИМЕННО в момент подмены, а не сразу: пока GLB летит по сети, игрок
 *  должен видеть хоть что-то, иначе противник на несколько секунд становится
 *  невидимым — это уже не косметика, а нечестное преимущество.
 *
 *  ⚠️ Материала-невидимки («рисуй только тень») здесь больше НЕТ. Он был
 *  заготовлен для тела от первого лица, но целиком невидимая модель — не то,
 *  что нужно: там надо спрятать всё, КРОМЕ ног. Это делает набор из
 *  `firstPersonKit`, а тень остаётся целой сама (см. `patchSkin`). */
export function makeSkin(group, kit, hide) {
  const skin = { group, kit, hide, model: null, bones: null, ready: false };
  loadCharacter();
  if (tpl) attach(skin); else pending.push(skin);
  return skin;
}

/** Тело удалено (респавн, выход из PVS, конец жизни трупа). */
export function dropSkin(skin) {
  if (!skin) return;
  skin.dead = true;
  const i = pending.indexOf(skin);
  if (i >= 0) pending.splice(i, 1);
  if (skin.model) skin.group.remove(skin.model);
}

// ---- Кадр: разворачиваем кости вдоль сегментов рига ----
const SEG_DIR = new THREE.Vector3(0, -1, 0);   // куда «висит» сегмент рига
const BONE_AXIS = new THREE.Vector3(0, 1, 0);  // куда растёт кость модели
// ⚠️ Разворот «лицом как у нас»: модель смотрит в +Z, наше тело — в −Z.
const Q_FACE = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);
// Точки покоя модели, за которые её держат (замер — tools/character-pack/rig.mjs).
const HIP_LOCAL = new THREE.Vector3(0, 0.971, -0.036);
const HEAD_LOCAL = new THREE.Vector3(0, 1.700, -0.017);
// ⚠️ Наклон корпуса вперёд в полном приседе. Число ПОДОБРАНО ЗАМЕРОМ, а не на
// глаз: наклон выносит грудь за коробку торса (она всего 0.36 м в глубину), а
// это «стрелял в грудь — не засчитано». Прогон `#check` в стенде: 0.30 давало
// 0.149 м наружу, 0.15 → 0.092, 0.08 → 0.059 (уровень, который и так есть
// в позе «лёжа» и не связан с наклоном). Больше не ставить.
const KNEEL_BEND = 0.08;
const PRONE_NECK = -0.55;      // подъём головы у лежащего (шея)
const PRONE_HEAD = -0.45;      // …и сама голова

/** Доля питча, которую отыгрывает ПОЗВОНОЧНИК. Без неё шея с головой кивают,
 *  а плечи стоят по стойке смирно — целящийся вниз выглядел бы сломанным.
 *
 *  ⚠️ Число упирается не в красоту, а в ХИТБОКСЫ, и потолок у него низкий.
 *  Серверные коробки на питч не реагируют вовсе (`hit.rs`), а наклон
 *  позвоночника ведёт голову по дуге: плечо рычага «поясница→голова» ≈ 0.55 м,
 *  то есть каждые 0.1 рад наклона уносят голову на 5.5 см при полукоробке
 *  головы 12 см. Больше — и «стрелял в голову, не засчитало». Замерено
 *  прогоном `#check` с питчем (см. md/models.md 2.3.8). Крутить только
 *  вместе с ним. */
const SPINE_PITCH = 0.05;

// ---- Покой: дыхание и перенос веса ----
//
// ⚠️ Идёт по СВОИМ часам `r.idle`, которые копит вызывающий (`updateRemotes`,
// `body.js`), а не по общему таймеру. Две причины. Первая: у каждого свои —
// стартуют со случайного места, иначе шестеро ботов дышат одной грудью.
// Вторая: расчётная проверка `#check` часов не крутит вовсе, и стенд меряет
// тело в НЕПОДВИЖНОЙ позе — иначе худший случай «вне коробок» гулял бы от
// прогона к прогону и перестал бы быть замером.
const BREATH_RATE = 1.5;       // рад/с — примерно 14 вдохов в минуту
const BREATH_BEND = 0.014;     // на вдохе грудь подаётся назад
const SWAY_RATE = 0.5;         // перенос веса втрое медленнее дыхания
const SWAY_ROLL = 0.020;       // …и это крен, а не сдвиг: сдвиг увёл бы ступни
/** Скорость, на которой покой кончается: идущий не «дышит», у него дыхание
 *  тонет в шаге. */
const STILL_SPEED = 0.7;
const _dir = new THREE.Vector3();
const _target = new THREE.Vector3();
const _anchor = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _qp = new THREE.Quaternion();
const _qw = new THREE.Quaternion();
const _qRoot = new THREE.Quaternion();
const _qAdd = new THREE.Quaternion();
const _e = new THREE.Euler();

/** Добавка к позе ПОКОЯ кости, а не подмена её (см. `skin.rest`). */
function bendBone(skin, name, x, z) {
  const bone = skin.bones[name], rest = skin.rest[name];
  if (!bone || !rest) return;
  bone.quaternion.copy(rest).multiply(_qAdd.setFromEuler(_e.set(x, 0, z)));
}

/** То же, но кость уже на руках (нужно для последнего звена конечности). */
function bendBoneQ(skin, bone, x) {
  const rest = skin.rest[bone.name];
  if (!rest) return;
  bone.quaternion.copy(rest).multiply(_qAdd.setFromEuler(_e.set(x, 0, 0)));
  bone.updateWorldMatrix(false, false);
}

/** На сколько кость пальцев НИЖЕ лодыжки в позе покоя модели (замер `rig.mjs`:
 *  лодыжка 0.086, пальцы 0.015). В этом наклоне подошва лежит на полу — то
 *  есть это и есть «стопа поставлена плоско», и опускать носок ниже нельзя
 *  ни при какой высоте лодыжки. Мерится ОТ ЛОДЫЖКИ, а не от нуля: у поднятой
 *  ноги пол ни при чём, а провалиться носком она всё равно не должна. */
const FOOT_DROP = 0.071;
const _p = new THREE.Vector3();
const _ankle = new THREE.Vector3();
const _dirF = new THREE.Vector3();
const _tgt = new THREE.Vector3();
const _qb = new THREE.Quaternion();
const _qbInv = new THREE.Quaternion();
const _qf = new THREE.Quaternion();
const _qLift = new THREE.Quaternion();
const _qFoot = new THREE.Quaternion();

/** Поставить ступню так, чтобы носок не оказался В ЗЕМЛЕ.
 *
 *  Зачем отдельно от `bendBoneQ`: у рига «ступня» — короткий отрезок ВНИЗ,
 *  от лодыжки до пола, и её наклон почти ничего не двигает; поэтому риг
 *  оставляет ей 40 % поворота ноги (`animateLimbs`) и коробкам это ничего не
 *  стоит. У модели та же кость идёт ВПЕРЁД, к пальцам, и вместе с кожей это
 *  ~20 см рычага: отведённая назад нога втыкает носок в песок на 5 см каждый
 *  шаг (замер `#check`, кость `ball_*`).
 *
 *  ⚠️ Лечится не углом шарнира, а НАПРАВЛЕНИЕМ самой кости: смотрим, куда
 *  реально смотрит отрезок «лодыжка → пальцы», и доворачиваем его до покойного
 *  наклона, сохранив азимут. Два способа проще этого уже проверены и оба
 *  не работают:
 *   • доворот по локальному углу X — в приседе нога сложена, ось стопы уходит
 *     из вертикальной плоскости, и поворот вокруг локальной X двигает носок
 *     В СТОРОНУ, а не вверх (под полом оставалось 11 см);
 *   • подмешивание позы покоя — в приседе она сама опускала носок НИЖЕ, чем
 *     риг (замер: носок 0.005 → −0.066), потому что «плоская стопа» относится
 *     к прямой ноге, а не к сложенной.
 *  Направление же не зависит ни от того, как сложена нога, ни от соглашений
 *  о знаках: минимальный поворот из «куда смотрит» в «куда надо».
 *
 *  Порог — покойный наклон стопы (`FOOT_DROP`), а не ноль: подошва в покое уже
 *  лежит на полу, и «поднять пальцы до нуля» значило бы поставить человека
 *  на пятки. Условие односторонее — задранный носок поднятой ноги не трогаем. */
function groundFoot(skin, foot, ball, group, base) {
  // ⚠️ Порядок: сперва ставим стопу как хочет риг, и только потом читаем
  // лодыжку. Мировая матрица ступни живая лишь после `bendBoneQ` — голень
  // к этому моменту уже повернулась, а ступня ещё висит с прошлого кадра.
  bendBoneQ(skin, foot, base);
  foot.updateWorldMatrix(false, true);
  foot.getWorldPosition(_ankle);
  group.worldToLocal(_ankle);
  ball.getWorldPosition(_p);
  group.worldToLocal(_p);
  _dirF.copy(_p).sub(_ankle);
  const len = _dirF.length();
  if (len < 1e-4 || _dirF.y >= -FOOT_DROP) return;      // носок не завален

  // Цель: тот же азимут, наклон — покойный. Горизонтальную длину берём из
  // длины кости, иначе доворот заодно растянул бы стопу.
  const hor = Math.hypot(_dirF.x, _dirF.z);
  const hy = Math.sqrt(Math.max(0, len * len - FOOT_DROP * FOOT_DROP));
  if (hor > 1e-6) _tgt.set((_dirF.x / hor) * hy, -FOOT_DROP, (_dirF.z / hor) * hy);
  else _tgt.set(0, -FOOT_DROP, -hy);
  _qLift.setFromUnitVectors(_dirF.divideScalar(len), _tgt.normalize());

  // Поворот посчитан в системе ТЕЛА — переносим его в мир сопряжением, иначе
  // он поедет по курсу игрока (ровно та ошибка, из которой вырос `poseBone`).
  group.getWorldQuaternion(_qb);
  _qbInv.copy(_qb).invert();
  foot.getWorldQuaternion(_qf);
  _qFoot.copy(_qb).multiply(_qLift).multiply(_qbInv).multiply(_qf);
  foot.parent.getWorldQuaternion(_qp).invert();
  foot.quaternion.copy(_qp.multiply(_qFoot));
  foot.updateWorldMatrix(false, true);
}

/** Поставить кость в ту же ориентацию, что у шарнира рига: `qJoint` — поворот
 *  шарнира в МИРЕ, `fix` — константа перехода «шарнир → кость» из позы
 *  привязки (см. `skin.fix`).
 *
 *  ⚠️ Берётся ВЕСЬ поворот шарнира, а не только направление сегмента, и это
 *  главная разница с прежней версией. Направление задаёт лишь ось кости;
 *  поворот вокруг самой оси при наведении по вектору не задан ничем и
 *  доставался «минимальным поворотом» three — а тот, во-первых, у почти
 *  вертикальной ноги вырожден (нога строго вниз и нога чуть вперёд получали
 *  твист, отличающийся на 180°), во-вторых, не эквивариантен повороту тела:
 *  ступни разворачивало ещё и по курсу игрока. Разбор — баг-урок
 *  «ступни живут своей жизнью» в md/models.md 2.3.10.
 *
 *  Здесь же ничего не теряется: у шарнира рига поворот полный (`rotation.x` —
 *  шаг, `rotation.z` — разведение ног при ползании, у трупа вообще
 *  произвольный кватернион), а `fix` переносит его в систему костей модели. */
function poseBone(bone, qJoint, fix) {
  if (!fix) return;
  bone.parent.getWorldQuaternion(_qp).invert();
  bone.quaternion.copy(_qp.multiply(_qw.copy(qJoint).multiply(fix)));
  bone.updateWorldMatrix(false, false);
}

/**
 * Повторить костями модели позу, которую только что посчитал риг.
 *
 * Зовётся ПОСЛЕДНИМ в кадре тела — после `layoutBody`, `applyHands` и
 * `animateLimbs`: те ставят риг, этот его копирует. Поменяешь порядок —
 * модель отстанет на кадр, и на быстром повороте это видно как «плывёт».
 *
 * Наклон и питч берём из полей, которые положил `layoutBody` (`_lean`/`_pitch`):
 * тащить их четвёртым и пятым аргументом пришлось бы через `animateLimbs`
 * и через оба её вызова — а поле уже есть и всегда свежее.
 *
 * @param {object} r тело (`buildBody`), у которого есть `skin`
 */
export function syncSkin(r) {
  const skin = r.skin;
  if (!skin || !skin.ready) return;
  const lean = r._lean || 0, pitch = r._pitch || 0;
  const prone = r.prone || 0, kneel = r.kneel || 0;

  // ---- Корень: кладём скелет на риг ----
  //
  // Позу берём ИЗ РИГА, а не считаем заново: `layoutBody` уже учёл и позу
  // (присед опускает таз, «лёжа» уводит тело назад), и наклон. Считать второй
  // раз — завести копию, которая однажды разъедется.
  //
  // ⚠️ Ось тела берётся ИЗ РИГА — по линии «таз → голова», а не поворотом на
  // фиксированные 90°. Жёсткий поворот не может сделать оба дела сразу: уложить
  // таз на землю И оставить голову в точке глаз. Причина в том, что коробочное
  // тело лёжа НЕ анатомично — от таза до головы у него 0.86 м, а у модели
  // позвоночник длиной 0.63. Ось же верна на всей дороге, включая переход:
  // встающий поднимается непрерывно, потому что риг поднимает его непрерывно.
  const hip = r.limbs.legL.root.position;
  _target.set(hip.x + SKIN_RIG.legSpan, hip.y, hip.z);
  _dir.copy(r.parts.head.position).sub(_target);
  const q = _qRoot.copy(Q_FACE);
  if (_dir.lengthSq() > 1e-6) {
    _dir.normalize();
    q.setFromUnitVectors(BONE_AXIS, _dir).multiply(Q_FACE);
  }
  skin.root.quaternion.copy(q);

  // ⚠️ ЯКОРЬ МЕНЯЕТСЯ С ПОЗОЙ. Стоя тело держится за БЕДРО: от него растут
  // ноги, и важно, чтобы ступни стояли на полу. Лёжа — за ГОЛОВУ: в точке глаз
  // сидит камера и оттуда же сервер пускает пулю (`hit::lean_point`), а ещё там
  // коробка хедшота — разъехаться нельзя вообще. Разницу длин (позвоночник
  // короче коробочного тела) забирает на себя таз: он оказывается ближе к
  // голове, чем коробка, а не наоборот. Это безопасная сторона: тело внутри
  // хитбокса, а не снаружи.
  _target.lerp(r.parts.head.position, prone);
  _anchor.copy(HIP_LOCAL).lerp(HEAD_LOCAL, prone).applyQuaternion(q);
  skin.root.position.copy(_target).sub(_anchor);

  // ---- Позвоночник и голова ----
  //
  // ⚠️ Стоя позвоночник остаётся в позе покоя намеренно: наш риг корпуса —
  // это три коробки, повторяющие ХИТБОКСЫ, а не позвоночник, и «гнуть кости
  // по коробкам» значило бы гнуть тело по границам зон урона. Гнём его только
  // там, где иначе получается неправда: крен наклона, наклон вперёд в приседе
  // и подъём головы у лежащего.
  const b = skin.bones;
  // ⚠️ ЗНАК ПИТЧА У КОСТЕЙ ОБРАТНЫЙ, и это баг-урок, а не педантизм.
  // У костей модели положительный поворот вокруг X кладёт лицо ВНИЗ (замер:
  // при `+pitch` голова уезжала вперёд-вниз, при `−pitch` откидывалась назад),
  // а положительный `pitch` в игре — это взгляд ВВЕРХ (камера: `pitchObject
  // .rotation.x = S.pitch`, и та же величина поднимает руки в `animateLimbs`).
  // То есть модель кивала ровно наоборот: ствол вниз — лицо вверх. Не видел
  // этого никто, потому что и стенд, и `#check` гоняли только питч = 0.
  // Коробочного тела это не касается: у коробки перед — локальная −Z, и там
  // тот же `+pitch` честно поднимает лицо (`parts.head.rotation.x`).
  const nod = -pitch;
  // Покой: дыхание и перенос веса. Гаснут на ходу и лёжа (лежащий дышит, но
  // грудью в землю — и это уже не видно вовсе).
  const still = (1 - Math.min(1, (r.speed || 0) / STILL_SPEED)) * (1 - prone);
  const t = r.idle || 0;
  const breath = Math.sin(t * BREATH_RATE) * BREATH_BEND * still;
  const sway = Math.sin(t * SWAY_RATE) * SWAY_ROLL * still;
  const roll = -(lean / LEAN_OFFSET_LOCAL) * SPINE_ROLL + sway;
  // Присев, человек подаётся вперёд — иначе выходит «сижу на стуле с прямой
  // спиной». Угол маленький: больше уводит грудь из коробки торса.
  // Туда же питч: корпус доворачивается за взглядом, иначе целящийся вниз
  // кивает головой при неподвижных плечах (см. SPINE_PITCH — потолок там
  // от хитбоксов, а не от вкуса).
  const bend = kneel * KNEEL_BEND + nod * SPINE_PITCH + breath;
  bendBone(skin, 'spine_01', bend * 0.4, roll * 0.35);
  bendBone(skin, 'spine_02', bend * 0.35, roll * 0.35);
  bendBone(skin, 'spine_03', bend * 0.25, roll * 0.30);
  // Голова кивает лишь долей угла — та же причина и та же доля, что у
  // коробочной головы: она зеркало хитбокса, который на питч не реагирует.
  // ⚠️ Лёжа к этому добавляется подъём: тело повёрнуто лицом в землю, и без
  // доворота лежащий утыкался бы носом в песок вместо того, чтобы смотреть
  // перед собой (и целиться).
  bendBone(skin, 'neck_01', nod * NECK_PITCH_SHARE + prone * PRONE_NECK, 0);
  bendBone(skin, 'Head', nod * HEAD_PITCH_SHARE_LOCAL + prone * PRONE_HEAD, 0);
  b.spine_01.updateWorldMatrix(false, true);

  // ---- Конечности: кость поворачивается так же, как шарнир рига ----
  //
  // ⚠️ ПОСЛЕДНЕЕ звено (ступня, кисть) разворачивается ИНАЧЕ — добавкой к позе
  // покоя. Причина найдена прогоном `#check`: у нашего рига «ступня» это
  // коробка, висящая вдоль −Y, то есть ВНИЗ, а у модели кость ступни смотрит
  // вперёд, к пальцам. Поверни её как сегмент рига — и персонаж встаёт
  // на цыпочки, а носки уходят под пол на 14 см. Глазами это не видно вовсе:
  // сверху нога выглядит нормальной, под землю уходит только носок.
  // Та же логика у кисти: она продолжает предплечье, и её собственный поворот
  // задан позой покоя, а не позой сегмента.
  for (const [name, chain] of skin.chains) {
    const joints = r.limbs[name].joints;
    const ball = name[0] === 'l' ? skin.bones[`ball_${name[3].toLowerCase()}`] : null;
    for (let i = 0; i < chain.length; i++) {
      if (i === chain.length - 1) {
        // Угол берём ЛОКАЛЬНЫЙ (относительно голени/предплечья) — ровно то,
        // что означает шарнир рига, — и кладём поверх покоя. У НОГИ поверх
        // этого носок ещё вынимается из земли (см. `groundFoot`).
        if (ball) groundFoot(skin, chain[i], ball, r.group, joints[i].rotation.x);
        else bendBoneQ(skin, chain[i], joints[i].rotation.x);
        continue;
      }
      // Поворот шарнира снимаем в МИРЕ, а `poseBone` переводит его в систему
      // родителя — поэтому разворот корня на π и поза группы учитываются сами.
      joints[i].getWorldQuaternion(_q);
      poseBone(chain[i], _q, skin.fix[chain[i].name]);
    }
  }
}
